import type { OrganizationMetadata } from '@/components/organizations/details/utils'
import { OrganizationRole } from '@/components/organizations/details/utils'
import { useOrganizations } from '@/components/organizations/hooks/useOrganizationsQuery'
import { useAuth } from '@/hooks/useAuth'
import { useOrganizationRole } from '@/hooks/useOrganizationRole'

const hasPortfolioManagerRole = (
  publicMetadata: Record<string, unknown> | undefined,
  organizationId: string | null
) => {
  if (!organizationId) {
    return false
  }

  const organizations = publicMetadata?.organizations as OrganizationMetadata[]

  if (!Array.isArray(organizations)) {
    return false
  }

  return organizations.some(
    (organization) =>
      organization.id === organizationId &&
      organization.role === OrganizationRole.PortfolioManager
  )
}

export const useAccessPermissions = (organizationId?: string) => {
  const { user } = useAuth()
  const {
    organizations: adminOrganizations,
    isPending: isAdminOrganizationsLoading,
  } = useOrganizations()
  const {
    isOrgLoaded,
    isOrgActive,
    hasOrgMembership,
    isOrgAdmin,
    organizationId: activeOrganizationId,
  } = useOrganizationRole()

  const scopedOrganizationId = organizationId ?? activeOrganizationId
  const isOrganizationLoading = !isOrgLoaded || isAdminOrganizationsLoading

  const hasAdminOrganizationMembership = adminOrganizations.length > 0
  const hasOrganizationMembership =
    (isOrgActive && hasOrgMembership) || hasAdminOrganizationMembership
  const hasPortfolioManagerOrganizationMembership = hasPortfolioManagerRole(
    user?.publicMetadata,
    scopedOrganizationId
  )
  const isAdminOfOrganization = organizationId
    ? adminOrganizations.some((org) => org.id === organizationId) ||
      (activeOrganizationId === organizationId && isOrgAdmin)
    : false
  const isOrganizationAdmin = organizationId
    ? isAdminOfOrganization
    : isOrgAdmin || hasAdminOrganizationMembership

  const isAccessDenied = !hasOrganizationMembership

  return {
    activeOrganizationId,
    isOrganizationLoading,
    hasOrganizationMembership,
    hasAdminOrganizationMembership,
    hasPortfolioManagerOrganizationMembership,
    adminOrganizations,
    isOrganizationAdmin,
    isAccessDenied,
  }
}
